import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NavigationStart, Router, RouterEvent } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GenericFormGroupComponent } from '../forms/generic-form-group.component';
import { AlertService } from '../shared/services/alert.service';
import { ReflectionService } from '../shared/utilities/reflection.service';
import { DialogData, DialogEvent, DialogEventComplete, FormGroupWhere } from './dialog.types';

@Component({
    selector: 'app-dialog-wrapper',
    templateUrl: './dialog-wrapper.component.html',
    styleUrls: ['./dialog-wrapper.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class DialogWrapperComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('wrapper') wrapper: ElementRef;
    @ViewChild('resizable') resizableRef: ElementRef;
    @ViewChild('dialogTitle') dialogTitleRef: ElementRef;
    @Output() panelClickedEvent: EventEmitter<string> = new EventEmitter<string>();
    loading: boolean;
    enableMultiWindow: boolean;
    private routerSubscription$: Subscription;
    private formGroups: Array<FormGroupWhere>;
    private backdropSubscription: Subscription;
    private keydownSubscription$: Subscription;
    private titleSubscription$: Subscription;
    constructor(
        public dialogRef: MatDialogRef<DialogWrapperComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData<any>,
        private alert: AlertService,
        private reflection: ReflectionService,
        private router: Router,
        private renderer2: Renderer2
    ) {
        this.enableMultiWindow = data?.enableMultiWindow;
        this.routerSubscription$ = router.events
            .pipe(
                filter((event: RouterEvent) => event instanceof NavigationStart),
                filter(() => !!this.dialogRef)
            )
            .subscribe(() => {
                this.dialogRef.close();
            });
    }

    ngOnInit(): void {
        this.backdropSubscription = this.dialogRef.backdropClick().subscribe((next) => {
            this.close();
        });
        if (this.enableMultiWindow) {
            this.keydownSubscription$ = this.dialogRef.keydownEvents().subscribe((next) => {
                if (next.code === 'Escape') {
                    this.close();
                } else if (next.code === 'Enter') {
                    next.preventDefault();
                    next.stopPropagation();
                }
            });
        }
    }

    ngAfterViewInit() {
        this.resizeObserve();
    }

    ngOnDestroy() {
        if (this.routerSubscription$) {
            this.routerSubscription$.unsubscribe();
        }
        if (this.backdropSubscription) {
            this.backdropSubscription.unsubscribe();
        }
        if (this.keydownSubscription$) {
            this.keydownSubscription$.unsubscribe();
        }
        if (this.titleSubscription$) {
            this.titleSubscription$.unsubscribe();
        }
    }

    resizeObserve() {
        if (!this.resizableRef || !this.dialogTitleRef) {
            return;
        }
        const resizeObserver = new ResizeObserver((entry, observer) => {
            if (entry[0].target == this.resizableRef.nativeElement) {
                const width = entry[0].target['offsetWidth'];
                this.renderer2.setStyle(this.dialogTitleRef.nativeElement, 'width', `${width}px`);
            }
        });
        resizeObserver.observe(this.resizableRef.nativeElement);
    }

    setTitleControlListener(control: UntypedFormControl, suffix?: string, prefix?: string) {
        if (this.titleSubscription$) {
            this.titleSubscription$.unsubscribe();
        }
        this.titleSubscription$ = control.valueChanges.subscribe((value) => {
            let update: string = '';
            if (suffix && prefix) {
                update = prefix + value + suffix;
            } else if (suffix) {
                update = value + suffix;
            } else if (prefix) {
                update = prefix + value;
            } else {
                update = value;
            }
            this.data.title = update;
        });
    }

    removeWhere(where: string) {
        if (!where) {
            return;
        }
        if (!this.formGroups || this.formGroups.length === 0) {
            return;
        }
        const index = this.formGroups.map((x) => x.where).indexOf(where);
        if (index >= 0) {
            this.formGroups.splice(index, 1);
        }
    }

    addFormGroup(group: GenericFormGroupComponent) {
        if (!group) {
            return;
        }
        this.formGroups = this.formGroups ? this.formGroups : [];
        const index = this.formGroups.map((x) => x.where).indexOf(group.getWhere());
        if (index >= 0) {
            this.formGroups.splice(index, 1);
        }
        this.formGroups.push({
            where: group.getWhere(),
            group: group
        });
    }

    close() {
        if (this.formGroups) {
            let msgArray: Array<string> = [];
            this.formGroups.forEach((fg) => {
                const m = fg.group.dirtyMessage();
                if (m) {
                    msgArray.push(m);
                }
            });
            if (msgArray.length) {
                msgArray = msgArray.filter((m) => m !== 'NO_MESSAGE');
                let text =
                    "Click 'OK' to continue without saving otherwise click 'Cancel' to go back and save your changes.";
                const msg = msgArray.length ? msgArray.join('<br/>') : '';
                if (msg) {
                    text += '<br/><br/>Your un-saved changes were in these areas:<br/>' + msg;
                }
                this.alert.confirm({
                    title: 'It looks like you have not saved your changes.',
                    text: text,
                    callBack: {
                        this: this,
                        function: (result: boolean) => {
                            if (result) {
                                this.dialogRef.close(this.data.uniqueId);
                            }
                        }
                    }
                });
            } else {
                this.dialogRef.close(this.data.uniqueId);
            }
        } else {
            this.dialogRef.close(this.data.uniqueId);
        }
    }
    refresh() {
        if (!this.data.refreshAction) {
            return;
        }
        this.loading = true;
        this.data.refreshAction.observable
            .call(this.data.refreshAction.this, {
                value: this.data.value
            } as DialogEvent<any>)
            .subscribe(
                (next) => {
                    const complete: DialogEventComplete = this.data.refreshAction.function.call(
                        this.data.refreshAction.this,
                        {
                            value: this.data.value,
                            observableValue: next
                        } as DialogEvent<any>
                    );
                    this.loading = false;
                },
                (error) => {
                    this.alert.toastError();
                    console.error(error);
                }
            );
    }

    bringToFront() {
        if (this.enableMultiWindow) {
            this.panelClickedEvent.emit(this?.data?.uniqueId);
        }
    }

    onResize($event: UIEvent) {
        console.log($event);
    }
}
